import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  GroupEntity,
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
  stringifyEntityRef,
  RELATION_HAS_MEMBER,
} from '@backstage/catalog-model';
import {
  Avatar,
  InfoCard,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import {
  EntityRefLinks,
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import Alert from '@material-ui/lab/Alert';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import GroupIcon from '@material-ui/icons/Group';
import { LinksGroup } from '../Meta';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { OnboardSquadsApiRef } from '../../../apis/onboardSquadsApi';
import { useScmIdentity } from '@internal/acc-react-shared';
import { identityApiRef } from '@backstage/core-plugin-api';

const CardTitle = (props: { title: string }) => (
  <Box display="flex" alignItems="center">
    <GroupIcon fontSize="inherit" />
    <Box ml={1}>{props.title}</Box>
  </Box>
);

function useGetEntity() {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [memberEntity, setMemberEntity] = useState<string | null>(null);

  useEffect(() => {
    const getAuthor = async () => {
      const identity = await identityApi.getBackstageIdentity();
      const foundEntity = await catalogApi.getEntityByRef(
        identity.userEntityRef,
      );
      setMemberEntity(foundEntity?.metadata.name ?? null);
    };
    getAuthor();
  }, [identityApi, catalogApi]);

  return memberEntity;
}

/** @public */
export const CustomGroupProfileCard = (props: {
  variant?: InfoCardVariants;
  showLinks?: boolean;
}) => {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const onboardSquadsApi = useApi(OnboardSquadsApiRef);
  const { entity: group } = useEntity<GroupEntity>();
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );
  const memberEntity = useGetEntity();
  const userToken = useScmIdentity();
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(group));
    alertApi.post({
      message: 'Refresh scheduled',
      severity: 'info',
      display: 'transient',
    });
  }, [catalogApi, alertApi, group]);

  const isUserInSquad = useMemo(() => {
    if (!group || !memberEntity) {
      return null;
    }

    const memberRelations = getEntityRelations(group, RELATION_HAS_MEMBER, {
      kind: 'User',
    });

    return memberRelations.some(member => member.name === memberEntity);
  }, [group, memberEntity]);

  if (!group) {
    return <Alert severity="error">Group not found</Alert>;
  }

  const {
    metadata: { name, description, title, annotations, links },
    spec: { profile },
  } = group;

  const childRelations = getEntityRelations(group, RELATION_PARENT_OF, {
    kind: 'Group',
  });
  const parentRelations = getEntityRelations(group, RELATION_CHILD_OF, {
    kind: 'group',
  });

  const entityLocation = annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const entityMetadataEditUrl =
    group.metadata.annotations?.[ANNOTATION_EDIT_URL];

  const displayName = profile?.displayName ?? title ?? name;
  const emailHref = profile?.email ? `mailto:${profile.email}` : '#';
  const infoCardAction = entityMetadataEditUrl ? (
    <IconButton
      aria-label="Edit"
      title="Edit Metadata"
      component={Link}
      to={entityMetadataEditUrl}
    >
      <EditIcon />
    </IconButton>
  ) : (
    <IconButton aria-label="Edit" disabled title="Edit Metadata">
      <EditIcon />
    </IconButton>
  );

  const handleJoinSquad = async () => {
    if (!entityMetadataEditUrl || !memberEntity) {
      alertApi.post({
        message: 'No URL or memberEntity was provided.',
        severity: 'error',
      });
      return;
    }
    setIsLoading(true);
    try {
      const result = await onboardSquadsApi.addUserToSquad(
        await userToken.getToken(entityMetadataEditUrl),
        entityMetadataEditUrl,
        memberEntity,
      );
      setPrUrl(result.prUrl);
      alertApi.post({ message: result.message, severity: 'success' });
    } catch (error) {
      alertApi.post({
        message: `Failed to join squad: ${error}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveSquad = async () => {
    if (!entityMetadataEditUrl || !memberEntity) {
      alertApi.post({
        message: 'No URL or memberEntity was provided.',
        severity: 'error',
      });
      return;
    }
    setIsLoading(true);
    try {
      const result = await onboardSquadsApi.deleteUserFromSquad(
        await userToken.getToken(entityMetadataEditUrl),
        entityMetadataEditUrl,
        memberEntity,
      );
      setPrUrl(result.prUrl);
      alertApi.post({ message: result.message, severity: 'success' });
    } catch (error) {
      alertApi.post({
        message: `Failed to leave squad: ${error}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const userColor = isUserInSquad ? 'secondary' : 'primary';
  const color = prUrl ? 'primary' : userColor;
  const userStatusText = isUserInSquad ? 'Leave Squad' : 'Join Squad';
  const buttonText = prUrl ? 'View Pull Request' : userStatusText;
  const userAction = isUserInSquad ? handleLeaveSquad : handleJoinSquad;
  const buttonAction = prUrl ? () => window.open(prUrl, '_blank') : userAction;

  return (
    <InfoCard
      title={<CardTitle title={displayName} />}
      subheader={description}
      variant={props.variant}
      action={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          {infoCardAction}
        </>
      }
    >
      <Grid container spacing={3} justifyContent="space-between">
        <Grid item xs={12} sm={2} xl={1}>
          <Avatar displayName={displayName} picture={profile?.picture} />
        </Grid>
        <Grid item md={10} xl={11}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <List>
              {profile?.email && (
                <ListItem>
                  <ListItemIcon>
                    <Tooltip title="Email">
                      <EmailIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Link to={emailHref}>{profile.email}</Link>}
                    secondary="Email"
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Parent Group">
                    <AccountTreeIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={
                    parentRelations.length ? (
                      <EntityRefLinks
                        entityRefs={parentRelations}
                        defaultKind="Group"
                      />
                    ) : (
                      'N/A'
                    )
                  }
                  secondary="Parent Group"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Child Groups">
                    <GroupIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={
                    childRelations.length ? (
                      <EntityRefLinks
                        entityRefs={childRelations}
                        defaultKind="Group"
                      />
                    ) : (
                      'N/A'
                    )
                  }
                  secondary="Child Groups"
                />
              </ListItem>
              {props?.showLinks && <LinksGroup links={links} />}
            </List>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2} // Add some margin-top for spacing
          >
            {entityMetadataEditUrl && (
              <Button
                variant="contained"
                color={color}
                onClick={buttonAction}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : buttonText}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
